import React, { useCallback, useEffect, useRef, useState } from 'react'

type Props = {
  children: React.ReactNode
  className?: string
  /** Extra classes on the scroll viewport (defaults include ss-mobile-scroll-tabs). */
  viewportClassName?: string
  ariaLabel?: string
}

/**
 * Horizontal scroller with always-visible scrollbar and left/right arrows
 * when content overflows.
 */
const SsHorizontalScroll = ({
  children,
  className = '',
  viewportClassName = '',
  ariaLabel = 'Scrollable content',
}: Props) => {
  const viewportRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateOverflow = useCallback(() => {
    const el = viewportRef.current
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    const maxScroll = scrollWidth - clientWidth
    setCanScrollLeft(scrollLeft > 2)
    setCanScrollRight(maxScroll > 2 && scrollLeft < maxScroll - 2)
  }, [])

  useEffect(() => {
    const el = viewportRef.current
    if (!el) return

    updateOverflow()

    const onScroll = () => updateOverflow()
    el.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', updateOverflow)

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateOverflow) : null
    ro?.observe(el)
    if (el.firstElementChild) ro?.observe(el.firstElementChild)

    // Re-check after fonts/layout settle
    const t1 = window.setTimeout(updateOverflow, 50)
    const t2 = window.setTimeout(updateOverflow, 300)

    return () => {
      el.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', updateOverflow)
      ro?.disconnect()
      window.clearTimeout(t1)
      window.clearTimeout(t2)
    }
  }, [updateOverflow, children])

  const scrollByAmount = (direction: -1 | 1) => {
    const el = viewportRef.current
    if (!el) return
    const amount = Math.max(160, Math.floor(el.clientWidth * 0.7))
    el.scrollBy({ left: direction * amount, behavior: 'smooth' })
  }

  const hasOverflow = canScrollLeft || canScrollRight

  return (
    <div
      className={`ss-hscroll ${hasOverflow ? 'ss-hscroll--overflow' : ''} ${className}`.trim()}
    >
      {canScrollLeft && (
        <button
          type='button'
          className='ss-hscroll__arrow ss-hscroll__arrow--left'
          aria-label='Scroll left'
          onClick={() => scrollByAmount(-1)}
        >
          <i className='fas fa-chevron-left' aria-hidden='true' />
        </button>
      )}

      <div
        ref={viewportRef}
        className={`ss-hscroll__viewport ss-mobile-scroll-tabs ${viewportClassName}`.trim()}
        role='region'
        aria-label={ariaLabel}
      >
        {children}
      </div>

      {canScrollRight && (
        <button
          type='button'
          className='ss-hscroll__arrow ss-hscroll__arrow--right'
          aria-label='Scroll right'
          onClick={() => scrollByAmount(1)}
        >
          <i className='fas fa-chevron-right' aria-hidden='true' />
        </button>
      )}
    </div>
  )
}

export default SsHorizontalScroll
